import { useState } from "react"

import { CheckCircleIcon } from "@heroicons/react/24/solid"
import { shapeAnnualToMonthlyTypical } from "@logic/lib/battery-arbitrage"

import type {
  BatteryProposalGenabilityAccountDto,
  BatteryProposalTariffCandidateDto,
} from "~/api/dto/battery-proposal.dto"
import useBatteryProposal from "~/api/hooks/useBatteryProposal"
import useBatteryProposalArbitrage from "~/api/hooks/useBatteryProposalArbitrage"
import useBatteryProposalGenabilityAccount from "~/api/hooks/useBatteryProposalGenabilityAccount"
import useBatteryProposalUtilityOptions from "~/api/hooks/useBatteryProposalUtilityOptions"
import useProposalConsumptionSelection from "~/api/hooks/useProposalConsumptionSelection"
import useUpdateBatteryProposalTariff from "~/api/hooks/useUpdateBatteryProposalTariff"
import useUpdateProposalConsumptionSelection from "~/api/hooks/useUpdateProposalConsumptionSelection"
import { ComboboxField, TextField } from "~/components/ui-components/v2"
import EnvVars from "~/env-vars"
import { parseAxiosError } from "~/utils/parseAxiosError"

import { savedAnnualEnergyUse } from "./battery-initial-setup-progress"
import { BatterySavingsSupport } from "./BatterySavingsSupport"
import { TariffSavingsIndicator } from "./TariffSavingsIndicator"

const MONTH_LABELS = [
  `Jan`,
  `Feb`,
  `Mar`,
  `Apr`,
  `May`,
  `Jun`,
  `Jul`,
  `Aug`,
  `Sep`,
  `Oct`,
  `Nov`,
  `Dec`,
] as const

const emptyMonths = (): number[] => Array.from({ length: 12 }, () => 0)

/**
 * Annual usage is entered to at most two decimals: a third decimal keystroke
 * is dropped rather than rejected on confirm, so the field never holds a
 * precision the savings math would silently round away.
 */
const limitToTwoDecimals = (raw: string): string => {
  const point = raw.indexOf(`.`)
  return point === -1 ? raw : raw.slice(0, point + 3)
}

const roundKwh = (value: number): number => Math.round(value * 100) / 100

/** Prefilled saved, typical, or shaped kWh obeys the same two-decimal limit. */
const kwhDisplayValue = (value: number): string => String(roundKwh(value))

const sumKwh = (values: number[]): number =>
  roundKwh(values.reduce((total, value) => total + value, 0))

/**
 * The twelve months the seller is editing. Seeding every month at the first
 * edit is what lets the annual total be their sum: the untouched months keep
 * the kWh they were already showing instead of being re-derived from an annual
 * that is itself derived from them.
 */
type MonthlyDraft = {
  /**
   * Exact kWh each month held when editing began. Untouched months are read
   * from here rather than from their displayed text, so opening the editor
   * never re-persists eleven months at the display's two-decimal precision.
   */
  base: number[]
  /** kWh text shown in each month field. */
  values: string[]
  /** Months the seller typed into; the rest stay provider-modeled. */
  touched: boolean[]
}

/**
 * A month is plain kWh digits with at most two decimals. `Number` alone would
 * take `1e4`, `0x64` and `+5` as usable kWh, which turns one fat-fingered
 * keystroke into a ten-thousand kWh month with nothing on screen to show for
 * it. A trailing point is allowed so the field is not red mid-keystroke, and
 * blank is refused: an empty field asserts nothing, and a stored zero would be
 * recorded as a month with no evidence behind it.
 */
const MONTH_KWH_PATTERN = /^\d{1,7}(\.\d{0,2})?$/

const parseMonthKwh = (raw: string): number | null => {
  const trimmed = raw.trim()
  return MONTH_KWH_PATTERN.test(trimmed) ? Number(trimmed) : null
}

const typicalMonthsFromAccount = (
  account: BatteryProposalGenabilityAccountDto | undefined,
): number[] => {
  const months = account?.typical?.monthlyEnergyUse
  return Array.isArray(months) && months.length === 12 ? months : []
}

const tariffLabel = (tariff: BatteryProposalTariffCandidateDto): string => {
  const name = tariff.tariffName?.trim() || tariff.tariffCode?.trim() || `Tariff`
  const utility = tariff.lseName?.trim()
  return utility ? `${name} · ${utility}` : name
}

/**
 * Tariff search matches the visible label plus the tariff code, so a seller
 * who knows the utility's code finds the rate without reading the whole list.
 */
const matchesTariffQuery = (
  tariff: BatteryProposalTariffCandidateDto,
  query: string,
): boolean => {
  const needle = query.trim().toLowerCase()
  if (needle === ``) {
    return true
  }
  return [tariffLabel(tariff), tariff.tariffCode].some(
    (candidate) => candidate?.toLowerCase().includes(needle) ?? false,
  )
}

/**
 * Utility Company, rate plan, and Consumption Profile confirmation. Shared by
 * the Utility setup drawer and inline setup; the container decides what
 * happens after a confirm.
 */
export const UtilityForm = ({
  proposalId,
  onConfirmed,
}: {
  proposalId: string
  /**
   * Runs after a successful confirm, and on the Continue action a container
   * gets when the saved selection is already valid and unchanged. The drawer
   * omits it and stays on the confirmed badge.
   */
  onConfirmed?: () => void
}): JSX.Element => {
  const savingsEnabled = EnvVars.BATTERY_SAVINGS_ENABLED
  const { data: proposal } = useBatteryProposal(proposalId)
  const {
    data: account,
    error: accountError,
    isError: isAccountError,
    isLoading: isAccountPending,
    refetch: refetchAccount,
  } = useBatteryProposalGenabilityAccount(proposalId)
  const updateTariff = useUpdateBatteryProposalTariff()
  const arbitrageQuery = useBatteryProposalArbitrage(proposalId, {
    enabled: savingsEnabled,
  })
  const { data: consumption } = useProposalConsumptionSelection(proposalId)
  const updateConsumption = useUpdateProposalConsumptionSelection()

  const [pickedTariffId, setPickedTariffId] = useState<number | null>(null)
  const [annualDraft, setAnnualDraft] = useState<string | null>(null)
  const [consumptionError, setConsumptionError] = useState<string | null>(null)
  const [changingTariff, setChangingTariff] = useState(false)
  const [pickedCompanyId, setPickedCompanyId] = useState<number | null>(null)
  const utilityOptions = useBatteryProposalUtilityOptions(
    proposalId,
    changingTariff,
    pickedCompanyId,
  )
  const [showMonths, setShowMonths] = useState(false)
  const [monthlyDraft, setMonthlyDraft] = useState<MonthlyDraft | null>(null)

  const savedAnnual = savedAnnualEnergyUse(consumption)
  const typicalAnnual = account?.typical?.annualEnergyUse
  const typicalMonths = typicalMonthsFromAccount(account)
  const savedMonths =
    consumption?.monthlyEnergyUse.length === 12
      ? consumption.monthlyEnergyUse
      : []
  // A seller who saved the year month by month outranks the regional typical:
  // re-scaling to a new annual must keep the shape of this home, not replace
  // it with the provider's.
  const savedFromMonths =
    consumption?.selectedProfile?.sourceKind === `MONTHLY_USAGE` &&
    savedMonths.some((value) => value > 0)
  const monthWeights = savedFromMonths
    ? savedMonths
    : typicalMonths.length === 12
      ? typicalMonths
      : savedMonths
  const annualInput =
    annualDraft ??
    (savedAnnual != null
      ? kwhDisplayValue(savedAnnual)
      : typicalAnnual != null && Number.isFinite(typicalAnnual)
        ? kwhDisplayValue(typicalAnnual)
        : ``)
  const parsedAnnualInput = Number(annualInput)
  const shapedMonths =
    Number.isFinite(parsedAnnualInput) && parsedAnnualInput >= 1
      ? shapeAnnualToMonthlyTypical({
          monthlyWeights: monthWeights,
          annualKwh: parsedAnnualInput,
        })
      : monthWeights.length === 12
        ? monthWeights
        : emptyMonths()
  // A saved year stands as saved. Re-shaping it to the regional typical on
  // every render would quietly discard a spread the seller entered by month.
  const presentedMonths =
    annualDraft == null && savedMonths.some((value) => value > 0)
      ? savedMonths
      : shapedMonths

  const draftMonthKwh =
    monthlyDraft?.base.map((value, index) =>
      monthlyDraft.touched[index]
        ? parseMonthKwh(monthlyDraft.values[index] ?? ``)
        : value,
    ) ?? null
  const unusableMonths = MONTH_LABELS.filter(
    (_, index) => draftMonthKwh != null && draftMonthKwh[index] == null,
  )
  const hasUnusableMonth = unusableMonths.length > 0
  const monthlyEnergyUse =
    draftMonthKwh?.map((value) => value ?? 0) ?? presentedMonths
  // Edited months own the year: the annual total is their sum, so the two
  // summaries the gateway checks for coherence can never drift apart.
  const annualEnergyUse =
    monthlyDraft != null
      ? kwhDisplayValue(sumKwh(monthlyEnergyUse))
      : annualInput
  const parsedAnnual = Number(annualEnergyUse)
  const monthFieldValues =
    monthlyDraft?.values ?? monthlyEnergyUse.map(kwhDisplayValue)

  const tariffs =
    pickedCompanyId != null
      ? (utilityOptions.tariffs.data ?? [])
      : (account?.tariffs ?? [])
  const savedCompanyId =
    proposal?.tariff?.lseId ?? account?.tariffs[0]?.lseId ?? null
  const companyId = pickedCompanyId ?? savedCompanyId
  const company =
    utilityOptions.companies.data?.find((entry) => entry.lseId === companyId) ??
    null
  const isCompanyDirty =
    pickedCompanyId != null && pickedCompanyId !== savedCompanyId
  const isTariffPreviewPending =
    pickedCompanyId != null && utilityOptions.tariffs.isFetching
  const isTariffPreviewError =
    pickedCompanyId != null && utilityOptions.tariffs.isError
  const confirmedTariffId = proposal?.tariff?.masterTariffId ?? null
  const pickedTariff =
    tariffs.find((tariff) => tariff.masterTariffId === pickedTariffId) ?? null
  const confirmedCandidate =
    tariffs.find((tariff) => tariff.masterTariffId === confirmedTariffId) ?? null
  const selectedTariff =
    pickedTariff ??
    (pickedCompanyId != null
      ? null
      : (confirmedCandidate ??
        (confirmedTariffId == null ? (tariffs[0] ?? null) : null)))

  const isTariffDirty =
    updateTariff.isError ||
    isCompanyDirty ||
    (selectedTariff != null &&
      selectedTariff.masterTariffId !== confirmedTariffId)
  const isConsumptionDirty =
    monthlyDraft != null
      ? hasUnusableMonth ||
        parsedAnnual !== savedAnnual ||
        savedMonths.length !== 12 ||
        monthlyEnergyUse.some((value, index) => value !== savedMonths[index])
      : annualDraft != null &&
        (!Number.isFinite(Number(annualDraft)) ||
          Number(annualDraft) !== savedAnnual)
  const isSaved = confirmedTariffId != null && savedAnnual != null
  const isBusy = updateTariff.isLoading || updateConsumption.isLoading
  const isConfirmed =
    isSaved &&
    !isTariffDirty &&
    !isConsumptionDirty &&
    !isBusy &&
    !(changingTariff && selectedTariff == null) &&
    !updateTariff.isError &&
    !updateConsumption.isError

  const utilityName =
    company?.name ||
    selectedTariff?.lseName?.trim() ||
    proposal?.tariff?.utilityCompanyName?.trim() ||
    null
  const selectedTariffName = selectedTariff
    ? (selectedTariff.tariffName?.trim() ??
      selectedTariff.tariffCode?.trim() ??
      `Tariff`)
    : confirmedTariffId != null && proposal?.tariff
      ? (proposal.tariff.tariffName ?? proposal.tariff.tariffCode ?? `Tariff`)
      : null

  const closeTariffPicker = (): void => {
    setPickedTariffId(null)
    setPickedCompanyId(null)
    setChangingTariff(false)
  }

  const editMonth = (index: number, raw: string): void => {
    setConsumptionError(null)
    setMonthlyDraft((current) => {
      const draft = current ?? {
        base: monthlyEnergyUse,
        values: monthFieldValues,
        touched: MONTH_LABELS.map(() => false),
      }
      const values = [...draft.values]
      values[index] = limitToTwoDecimals(raw)
      const touched = [...draft.touched]
      touched[index] = true
      return { ...draft, values, touched }
    })
  }

  const confirmUtilityInfo = (): void => {
    if (
      changingTariff &&
      (!selectedTariff || isTariffPreviewPending || isTariffPreviewError)
    ) {
      return
    }
    if (hasUnusableMonth) {
      setConsumptionError(
        `Enter ${unusableMonths.join(`, `)} as kWh, to at most two decimals.`,
      )
      return
    }

    const annual = Number(annualEnergyUse)
    if (!Number.isFinite(annual) || annual < 1) {
      setConsumptionError(`Enter at least 1 kWh before confirming.`)
      return
    }

    setConsumptionError(null)
    void (async () => {
      let saved = false
      try {
        if (selectedTariff && isTariffDirty) {
          await updateTariff.mutateAsync({
            proposalId,
            body: {
              masterTariffId: selectedTariff.masterTariffId,
              ...(selectedTariff.lseId != null
                ? { lseId: selectedTariff.lseId }
                : {}),
            },
          })
        }
        if (
          consumption?.selectedProfile == null ||
          savedAnnual == null ||
          isConsumptionDirty
        ) {
          await updateConsumption.mutateAsync({
            proposalId,
            requestId: crypto.randomUUID(),
            data: {
              inputMode: monthlyDraft ? `monthly_usage` : `annual_usage`,
              annualEnergyUse: annual,
              monthlyEnergyUse,
              monthlyEnergyCost: emptyMonths(),
              // Only a month the seller typed into is seller-entered. An
              // annual-only save types no month at all, so its twelve months
              // are wholly provider-modeled — claiming otherwise would leave
              // the recorded basis unable to tell the two apart.
              monthlyEnergyManualFlags:
                monthlyDraft?.touched ?? MONTH_LABELS.map(() => false),
              monthlyEnergyEntryUnits: MONTH_LABELS.map((_, index) =>
                monthlyDraft?.touched[index] ? (`kwh` as const) : null,
              ),
            },
          })
        }
        setChangingTariff(false)
        setPickedCompanyId(null)
        setPickedTariffId(null)
        // The saved year is now the server's. Dropping both drafts leaves the
        // form reading committed state rather than a stale local shape.
        setMonthlyDraft(null)
        setAnnualDraft(null)
        // Consumption alone is not a confirmed step. With no tariff candidates
        // the save still lands, but there is no rate plan for the savings math
        // to run on, so a container must not be told to move past Utility.
        saved = confirmedTariffId != null || selectedTariff != null
      } catch {
        // Mutation errors surface below through React Query state.
      }

      // Outside the catch: a container's continuation must not be mistaken
      // for a save failure, and its throw must not be swallowed here.
      if (saved) {
        onConfirmed?.()
      }
    })()
  }

  return (
    <div className="flex flex-col gap-6" data-testid="utility-step">
      <p className="text-sm text-muted">
        Review what we detected for this home, correct anything that&apos;s off,
        and confirm.
        {savingsEnabled ? ` This is what the savings math runs on.` : null}
      </p>

      <div className="space-y-5 rounded-xl border border-border bg-surface p-5">
        <div className="space-y-1">
          <p className="text-xs font-semibold tracking-wide text-muted uppercase">
            {utilityName ?? `Utility`}
          </p>
          {isAccountPending ? (
            <div
              className="h-10 animate-pulse rounded-md bg-muted"
              role="status"
              aria-label="Loading tariffs"
            />
          ) : isAccountError ? (
            <div
              className="space-y-3 rounded-xl border border-danger/40 bg-danger/10 p-4"
              role="alert"
            >
              <p className="text-sm text-foreground">
                {parseAxiosError(
                  accountError,
                  `The tariff list could not be loaded.`,
                )}
              </p>
              <button
                className="text-sm font-semibold text-primary underline"
                onClick={() => {
                  void refetchAccount()
                }}
                type="button"
              >
                Retry
              </button>
            </div>
          ) : changingTariff ? (
            <div className="space-y-4">
              <ComboboxField
                label="Utility Company"
                portal={false}
                options={utilityOptions.companies.data ?? []}
                value={company}
                placeholder={utilityName ?? `Select a Utility Company`}
                getKey={(entry) => entry.lseId}
                getLabel={(entry) => entry.name}
                disabled={isBusy || utilityOptions.companies.isFetching}
                onChange={(entry) => {
                  if (!entry) return
                  setPickedCompanyId(entry.lseId)
                  setPickedTariffId(null)
                }}
                emptyMessage="No Utility Companies are available for this location."
              />
              {utilityOptions.companies.isError ? (
                <div role="alert" className="text-sm text-danger">
                  Utility Companies could not be loaded.
                  <button
                    type="button"
                    className="ml-2 underline"
                    onClick={() => void utilityOptions.companies.refetch()}
                  >
                    Retry companies
                  </button>
                </div>
              ) : null}
              {isTariffPreviewError ? (
                <div role="alert" className="text-sm text-danger">
                  Tariffs could not be loaded for this Utility Company.
                  <button
                    type="button"
                    className="ml-2 underline"
                    onClick={() => void utilityOptions.tariffs.refetch()}
                  >
                    Retry tariffs
                  </button>
                </div>
              ) : null}
              <ComboboxField
                className="grow"
                label="Utility tariff"
                portal={false}
                disabled={
                  isBusy || isTariffPreviewPending || isTariffPreviewError
                }
                placeholder={
                  isTariffPreviewPending ? `Loading tariffs…` : `Select a tariff`
                }
                options={tariffs}
                value={selectedTariff}
                onChange={(tariff) => {
                  if (tariff == null) {
                    return
                  }

                  setPickedTariffId(tariff.masterTariffId)
                }}
                getKey={(tariff) => tariff.masterTariffId}
                getLabel={tariffLabel}
                renderOption={(tariff) => (
                  <span className="flex flex-col gap-1 whitespace-normal normal-case">
                    <span>{tariffLabel(tariff)}</span>
                    {savingsEnabled ? (
                      <TariffSavingsIndicator
                        hasGeneralizedRateScenario={
                          tariff.hasGeneralizedRateScenario === true
                        }
                        support={
                          tariff.masterTariffId === confirmedTariffId &&
                          !isConsumptionDirty &&
                          !isBusy &&
                          !arbitrageQuery.isFetching &&
                          !arbitrageQuery.isError
                            ? arbitrageQuery.data?.support
                            : undefined
                        }
                      />
                    ) : null}
                  </span>
                )}
                description={
                  savingsEnabled
                    ? `Indicators describe savings estimates, not rate-plan eligibility. Generalized scenarios still need suitable usage; unchecked plans are not necessarily unsupported.`
                    : undefined
                }
                filter={matchesTariffQuery}
                emptyMessage={`No tariffs match that search.`}
              />
              {pickedCompanyId != null &&
              !isTariffPreviewPending &&
              !isTariffPreviewError &&
              tariffs.length === 0 ? (
                <p className="text-sm text-muted">
                  No residential tariffs are available for this Utility Company.
                  Choose another company or cancel.
                </p>
              ) : null}
              <button
                disabled={isBusy}
                className="shrink-0 text-sm font-semibold text-muted underline hover:text-foreground"
                onClick={closeTariffPicker}
                type="button"
              >
                Cancel
              </button>
            </div>
          ) : selectedTariffName == null ? (
            <div className="space-y-2">
              <p className="text-sm text-muted">
                No tariffs are available for this location.
              </p>
              <button
                className="text-sm font-semibold text-primary underline"
                type="button"
                onClick={() => setChangingTariff(true)}
              >
                Change Utility Company
              </button>
            </div>
          ) : (
            <div className="flex items-baseline justify-between gap-3">
              <p className="font-display text-xl font-semibold text-foreground">
                {selectedTariffName}
              </p>
              {!isBusy ? (
                <button
                  className="shrink-0 text-sm font-semibold text-primary underline"
                  onClick={() => setChangingTariff(true)}
                  type="button"
                >
                  Not right? Change
                </button>
              ) : null}
            </div>
          )}
          {!isAccountPending &&
          !isAccountError &&
          !changingTariff &&
          selectedTariff &&
          confirmedTariffId == null ? (
            <p className="text-xs text-muted">Best match for this address</p>
          ) : null}
        </div>

        <div className="h-px bg-border" />

        <div className="space-y-2">
          <TextField
            label="Annual usage"
            trailingAddon="kWh"
            inputMode="decimal"
            // Edited months own the year, so the total is theirs to state, not
            // the seller's to overtype. Leaving it writable meant one stray
            // keystroke here silently threw away all twelve entered months.
            readOnly={monthlyDraft != null}
            value={annualEnergyUse}
            onChange={(event) => {
              setConsumptionError(null)
              setAnnualDraft(limitToTwoDecimals(event.target.value))
            }}
          />
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {/* Before the account resolves there is no spread to seed from,
                and a grid of twelve zeros would let one entry pin a year the
                arriving typical could no longer correct. */}
            {isAccountPending ? null : (
              <button
                className="text-xs font-semibold text-primary underline"
                onClick={() => setShowMonths((value) => !value)}
                type="button"
              >
                {showMonths ? `Hide monthly usage` : `Edit monthly usage`}
              </button>
            )}
            {monthlyDraft ? (
              <button
                className="text-xs font-semibold text-muted underline hover:text-foreground"
                onClick={() => {
                  setConsumptionError(null)
                  setMonthlyDraft(null)
                }}
                type="button"
              >
                Discard monthly edits and re-spread from annual usage
              </button>
            ) : null}
          </div>
          {showMonths && !isAccountPending ? (
            <div className="space-y-2">
              <p className="text-xs font-semibold tracking-wide text-muted uppercase">
                Monthly usage (kWh)
              </p>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {MONTH_LABELS.map((label, index) => (
                  <TextField
                    key={label}
                    label={label}
                    inputMode="decimal"
                    value={monthFieldValues[index] ?? ``}
                    error={
                      draftMonthKwh != null && draftMonthKwh[index] == null
                        ? `kWh only`
                        : undefined
                    }
                    onChange={(event) => editMonth(index, event.target.value)}
                  />
                ))}
              </div>
              <p className="text-xs text-muted">
                {monthlyDraft
                  ? `Annual usage is the total of these months.`
                  : savedFromMonths
                    ? `Monthly spread follows the usage entered for this home. Changing the annual total re-scales it.`
                    : typicalMonths.length === 12
                      ? `Monthly spread follows the regional typical for this address. Edit any month to enter this home's own usage.`
                      : `Edit any month to enter this home's own usage.`}
              </p>
            </div>
          ) : monthlyDraft == null && savedFromMonths ? (
            <p className="text-xs text-muted">
              Monthly spread follows the usage entered for this home.
            </p>
          ) : typicalMonths.length === 12 && monthlyDraft == null ? (
            <p className="text-xs text-muted">
              Monthly spread follows the regional typical for this address.
            </p>
          ) : null}
        </div>
      </div>

      {savingsEnabled ? (
        <BatterySavingsSupport
          proposalId={proposalId}
          hasUnsavedInputs={isTariffDirty || isConsumptionDirty || isBusy}
        />
      ) : null}

      <div className="flex items-center gap-3">
        {isConfirmed ? (
          <>
            <div className="flex items-center gap-2 text-sm font-semibold text-success">
              <CheckCircleIcon className="h-5 w-5" />
              Utility info confirmed
            </div>
            {/* A container that continues past this step needs an action even
                when the saved selection needs no edit. It holds the same
                usage floor a confirm enforces. */}
            {onConfirmed && parsedAnnual >= 1 ? (
              <button
                className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
                onClick={() => onConfirmed()}
                type="button"
              >
                Continue
              </button>
            ) : null}
          </>
        ) : (
          <button
            className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            disabled={
              isBusy ||
              isAccountPending ||
              (changingTariff &&
                (selectedTariff == null ||
                  isTariffPreviewPending ||
                  isTariffPreviewError))
            }
            onClick={confirmUtilityInfo}
            type="button"
          >
            {isBusy
              ? `Confirming…`
              : isSaved
                ? `Update utility info`
                : `Confirm utility info`}
          </button>
        )}
      </div>
      {updateTariff.isError ? (
        <p className="text-sm text-danger" role="alert">
          {parseAxiosError(
            updateTariff.error,
            `The tariff could not be confirmed.`,
          )}
        </p>
      ) : null}
      {consumptionError ? (
        <p className="text-sm text-danger" role="alert">
          {consumptionError}
        </p>
      ) : null}
      {updateConsumption.isError ? (
        <p className="text-sm text-danger" role="alert">
          {parseAxiosError(
            updateConsumption.error,
            `Consumption could not be saved.`,
          )}
        </p>
      ) : null}
    </div>
  )
}
